/**
 * Content List Page - Clean Design
 */

import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAppStore } from '../stores';

const API_URL = '';

interface ContentItem {
    id: string;
    title?: string;
    name?: string;
    status?: string;
    created_at?: number;
    updated_at?: number;
    [key: string]: unknown;
}

export function ContentList() {
    const { collectionId } = useParams();
    const navigate = useNavigate();
    const { collections } = useAppStore();
    const [content, setContent] = useState<ContentItem[]>([]);
    const [loading, setLoading] = useState(true);

    const collection = collections.find((c) => c.id === collectionId);

    useEffect(() => {
        if (collection) {
            loadContent();
        }
    }, [collection, collectionId]);

    const loadContent = async () => {
        if (!collection) return;

        setLoading(true);
        try {
            const slug = collection.slug || collection.tableName;
            const response = await fetch(`${API_URL}/api/v1/content/${slug}`);
            const result = await response.json();
            if (result.success) {
                setContent(result.data || []);
            }
        } catch (error) {
            console.error('Failed to load content:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!confirm('Are you sure you want to delete this item?')) return;

        try {
            const slug = collection?.slug || collection?.tableName;
            await fetch(`${API_URL}/api/v1/content/${slug}/${id}`, { method: 'DELETE' });
            setContent(content.filter(c => c.id !== id));
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    const formatDate = (timestamp?: number) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleDateString([], {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (!collection) {
        return (
            <div className="animate-fade-in py-12">
                <div className="card p-12 text-center max-w-md mx-auto">
                    <p style={{ color: 'var(--text-secondary)' }} className="mb-6">
                        Collection not found. It may have been deleted.
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="btn btn-primary w-full"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <span className="text-2xl">{collection.icon || '📄'}</span>
                        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                            {collection.name}
                        </h1>
                    </div>
                    <p style={{ color: 'var(--text-secondary)' }}>
                        {collection.description || `Manage all ${collection.name.toLowerCase()} entries`}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button className="btn btn-secondary" onClick={loadContent} title="Refresh">
                        🔄
                    </button>
                    <Link to={`/collections/${collectionId}/new`} className="btn btn-primary">
                        + New Entry
                    </Link>
                </div>
            </div>

            {/* Main Content */}
            <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                            <tr>
                                <th className="px-6 py-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
                                    Entry
                                </th>
                                <th className="px-6 py-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
                                    Status
                                </th>
                                <th className="px-6 py-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
                                    Last Updated
                                </th>
                                <th className="px-6 py-4 font-semibold text-sm text-right" style={{ color: 'var(--text-secondary)' }}>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center">
                                        <div className="flex items-center justify-center gap-3" style={{ color: 'var(--text-muted)' }}>
                                            <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                                            Loading entries...
                                        </div>
                                    </td>
                                </tr>
                            ) : content.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-16 text-center">
                                        <div className="text-4xl mb-4">📭</div>
                                        <p style={{ color: 'var(--text-secondary)' }} className="mb-6">
                                            No entries found for this collection.
                                        </p>
                                        <Link to={`/collections/${collectionId}/new`} className="btn btn-primary inline-flex">
                                            Create First Entry
                                        </Link>
                                    </td>
                                </tr>
                            ) : (
                                content.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="hover:bg-opacity-50 transition-colors cursor-pointer"
                                        style={{ backgroundColor: 'transparent' }}
                                        onClick={() => navigate(`/collections/${collectionId}/${item.id}`)}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                                                {String(item.title || item.name || item.id)}
                                            </div>
                                            <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                                ID: {item.id}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-2.5 py-1 text-xs font-medium rounded-full" style={{
                                                backgroundColor: item.status === 'published' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(100, 116, 139, 0.15)',
                                                color: item.status === 'published' ? 'var(--success)' : 'var(--text-secondary)'
                                            }}>
                                                {item.status || 'draft'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                            {formatDate(item.updated_at || item.created_at)}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Link
                                                    to={`/collections/${collectionId}/${item.id}`}
                                                    className="btn btn-ghost p-2 rounded-lg"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    ✏️
                                                </Link>
                                                <button
                                                    onClick={(e) => handleDelete(item.id, e)}
                                                    className="btn btn-ghost p-2 rounded-lg hover:text-red-500"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer / Pagination Placeholder */}
                {!loading && content.length > 0 && (
                    <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)' }}>
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            Showing {content.length} entries
                        </span>
                        <div className="flex gap-2">
                            <button className="btn btn-secondary py-1 px-3 text-xs disabled:opacity-30" disabled>Previous</button>
                            <button className="btn btn-secondary py-1 px-3 text-xs disabled:opacity-30" disabled>Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
